import { FilterDropdown as HouseFilterDropdown, type FilterOption } from '@/components/glitch-ui/filter-dropdown';
import { useNotificationCountsQuery } from '@/lib/query-options';

/**
 * The notifications filter (#6716).
 *
 * This was a second filter dropdown, written out in full beside the house one
 * and drifting from it: a cobalt count badge where the house badge is grey, and
 * an Apply button whose label grew by the count, so it changed width. It is now
 * an adapter: the house `FilterDropdown` draws it, and this file holds only what
 * is particular to notifications, the source and level lists and their counts.
 *
 * The props are unchanged, so both callers, the chips bar and the notification
 * detail page, are untouched.
 */

const SOURCES = [
  { value: 'ACTIONS', label: 'Actions', color: 'text-blue-800' },
  { value: 'CONFIGURATION', label: 'Configuration', color: 'text-purple-800' },
  { value: 'CAMPAIGN_CREATION', label: 'Campaign Creation', color: 'text-amber-800' },
  { value: 'onboarding', label: 'Onboarding', color: 'text-teal-800' },
  { value: 'keyword_sync', label: 'Policy Violations', color: 'text-red-800' },
  { value: 'googleads_campaign_sync', label: 'Google Ads Sync', color: 'text-orange-800' },
];

// Report notifications fold under one parent row, so the list stays scannable
// and "every report" is one tick. The house component draws a parent with
// children exactly this way.
const REPORT_SOURCES = [
  { value: 'WEEKLY_PERFORMANCE_REPORT', label: 'Weekly Reports' },
  { value: 'daily_summary', label: 'Daily Summary' },
  { value: 'strategic_report', label: '6 Month Strategic' },
  { value: 'intelligence_report', label: 'Intelligence' },
];

const WARNING_LEVELS = [
  { value: 'ERROR', label: 'Error', color: 'text-red-800' },
  { value: 'WARNING', label: 'Warning', color: 'text-yellow-800' },
  { value: 'SUCCESS', label: 'Success', color: 'text-green-800' },
  { value: 'INFO', label: 'Info', color: 'text-gray-900' },
];

interface FilterDropdownProps {
  organizationId: number;
  selectedSources: string[];
  selectedWarningLevels: string[];
  startDate?: string;
  endDate?: string;
  activeFilterCount: number;
  onApplyFilters: (filters: {
    sources: string[];
    warningLevels: string[];
    startDate?: string;
    endDate?: string;
  }) => void;
}

export function FilterDropdown({
  organizationId,
  selectedSources,
  selectedWarningLevels,
  startDate,
  endDate,
  activeFilterCount,
  onApplyFilters,
}: FilterDropdownProps) {
  const { data: counts } = useNotificationCountsQuery(organizationId, { is_archived: false });

  const withCount = (option: { value: string; label: string; color?: string }, by: 'source' | 'level'): FilterOption => ({
    ...option,
    count: (by === 'source' ? counts?.by_source?.[option.value] : counts?.by_warning_level?.[option.value]) || 0,
  });

  return (
    <HouseFilterDropdown
      sections={[
        {
          id: 'sources',
          title: 'Source',
          options: [
            ...SOURCES.map((s) => withCount(s, 'source')),
            {
              value: 'reports',
              label: 'Reports',
              children: REPORT_SOURCES.map((r) => withCount(r, 'source')),
            },
          ],
        },
        {
          id: 'warningLevels',
          title: 'Level',
          options: WARNING_LEVELS.map((l) => withCount(l, 'level')),
        },
      ]}
      selectedValues={{ sources: selectedSources, warningLevels: selectedWarningLevels }}
      startDate={startDate}
      endDate={endDate}
      activeFilterCount={activeFilterCount}
      onApply={({ selections, startDate: from, endDate: to }) =>
        onApplyFilters({
          sources: selections.sources ?? [],
          warningLevels: selections.warningLevels ?? [],
          startDate: from,
          endDate: to,
        })
      }
    />
  );
}
